import axios from "axios";
import JSZip from "jszip";
import dayjs from "dayjs";
import { __API_URL__ } from "../SERVER_URL";
import { buildEcrfPdfFilename, generateEcrfPdfBlob } from "./ecrfPdfUtils";

// signal is an AbortSignal: passed straight through to each axios call so an in-flight request
// to get_ecrf_blocks_per_visit gets cancelled immediately, and checked at the top of every loop
// iteration so a cancellation lands between visits too, not just mid-request. Returns
// { aborted: true } instead of a zip if the export was cancelled partway through.
export const generateTrialArchiveEcrfZip = async ({
  trialId,
  token,
  onProgress,
  signal,
  patientStatus,
}) => {
  const manifestRes = await axios.post(
    `${__API_URL__}/api/ecrf/ecrf_filled_pdf_manifest/`,
    { trials: [{ id: trialId }], ...(patientStatus ? { patient_status: patientStatus } : {}) },
    { headers: { Authorization: `Token ${token}` }, signal }
  );
  const manifest = manifestRes.data.results;

  if (!manifest?.length) {
    return { zipBlob: null, failedVisits: [] };
  }

  const zip = new JSZip();
  const timestamp = dayjs().format("YYYYMMDDTHHmmss");
  const failedVisits = [];

  onProgress?.({ current: 0, total: manifest.length });

  let completed = 0;
  const processVisit = async (entry) => {
    try {
      const res = await axios.get(
        `${__API_URL__}/api/ecrf/get_ecrf_blocks_per_visit/${entry.visit_id}/?patient_id=${entry.patient_id}&session_id=${entry.session_id}`,
        { headers: { Authorization: `Token ${token}` }, signal }
      );
      const blob = await generateEcrfPdfBlob(res.data.results, {
        trialName: entry.trial_name,
        visitName: entry.visit_name,
        siteId: entry.site_id,
        participantId: entry.participant_id,
      });
      const filename = buildEcrfPdfFilename(
        entry.trial_name,
        entry.site_id,
        entry.participant_id,
        entry.visit_name,
        timestamp
      );
      zip.folder(`${entry.site_id}/${entry.participant_id}`).file(`${filename}.pdf`, blob);
    } catch {
      if (!signal?.aborted) {
        failedVisits.push(`${entry.participant_id} — ${entry.visit_name}`);
      }
    }
    completed += 1;
    onProgress?.({ current: completed, total: manifest.length });
  };

  // Hand-rolled concurrency-limited worker pool: each worker pulls the next unclaimed index and
  // awaits one visit at a time, so at most DEFAULT_CONCURRENCY visits are in flight together.
  // Sized for the Export Study path (bounded by the platform's 2-slot concurrency cap); the
  // unrelated Archive Trial feature also calls this with no cap of its own, but is admin-gated
  // and used far less frequently.
  const DEFAULT_CONCURRENCY = 4;
  let nextIndex = 0;
  const workers = Array.from(
    { length: Math.min(DEFAULT_CONCURRENCY, manifest.length) },
    async () => {
      while (nextIndex < manifest.length) {
        if (signal?.aborted) return;
        const entry = manifest[nextIndex++];
        await processVisit(entry);
      }
    }
  );
  await Promise.all(workers);

  if (signal?.aborted) {
    return { zipBlob: null, failedVisits, aborted: true };
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  return { zipBlob, failedVisits };
};

export const uploadTrialArchiveEcrfZip = async ({ trialId, zipBlob, token }) => {
  const presignedRes = await axios.post(
    `${__API_URL__}/api/photos/get_presigned_urls/`,
    {
      uploadType: "trial_archive_ecrf_pdf",
      trial_id: trialId,
      files: [{ name: "ecrf_filled_pdfs.zip", type: "application/zip" }],
    },
    { headers: { Authorization: `Token ${token}` } }
  );
  const [{ upload_url, blob_path, content_type }] = presignedRes.data.urls;

  const res = await fetch(upload_url, {
    method: "PUT",
    headers: { "Content-Type": content_type },
    body: zipBlob,
  });
  if (!res.ok) {
    throw new Error("Failed to upload eCRF PDF zip.");
  }

  return blob_path;
};

export const uploadEcrfFilledPdfZip = async ({ zipBlob, token, signal }) => {
  const presignedRes = await axios.post(
    `${__API_URL__}/api/photos/get_presigned_urls/`,
    {
      uploadType: "study_export_ecrf_pdf",
      files: [{ name: "ecrf_filled_pdfs.zip", type: "application/zip" }],
    },
    { headers: { Authorization: `Token ${token}` }, signal }
  );
  const [{ upload_url, blob_path, content_type }] = presignedRes.data.urls;

  const res = await fetch(upload_url, {
    method: "PUT",
    headers: { "Content-Type": content_type },
    body: zipBlob,
    signal,
  });
  if (!res.ok) {
    throw new Error("Failed to upload eCRF PDF zip.");
  }

  return blob_path;
};
